import React, { useEffect, useState } from "react";
import {

  GoogleMap,
  Marker,
  OverlayView,

  DirectionsRenderer,
  useLoadScript,
} from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDriversLicense, } from "@fortawesome/free-solid-svg-icons";
import { GoogleMapStyle, DELIVERY_STATUS } from "../../../config/constants";
import {
  DriverLocationIcon,
  DropoffLocationIcon,
  PickupLocationIcon,

} from "../../../config/googlemapstyle";
import StatusMessage from "./StatusMessage";

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
  maxHeight: "600px",
  borderRadius: "8px", // Adds rounded corners
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Soft shadow for a polished effect
};

const mapHidden = [DELIVERY_STATUS.CANCELLED, DELIVERY_STATUS.COMPLETED];

const DeliveryMapStatus = ({ delivery }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [status, setStatus] = useState(delivery.status);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [mapData, setMapData] = useState({});

  const handleStatusFlow = (status) => {
    switch (status) {
      case DELIVERY_STATUS.REQUESTED:
        return "Sit tight! We're finding the best driver for your delivery.";
      case DELIVERY_STATUS.FINDING:
        return "Hang on, we’re on the lookout for a driver!";
      case DELIVERY_STATUS.ASSIGNED:
        return "Great news! A driver is assigned and heading to your location.";
      case DELIVERY_STATUS.HEADING_TO_PICKUP:
        return "Your driver is on the way to the pickup location!";
      case DELIVERY_STATUS.PICKUP_ARRIVED:
        return "Your driver has arrived at the pickup location.";
      case DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF:
        return "Your driver is en route to the drop-off location!";
      case DELIVERY_STATUS.DROPOFF_ARRIVED:
        return "Your driver has arrived at the drop-off location.";
      case DELIVERY_STATUS.EN_ROUTE_RETURN:
        return "Your driver is bringing your pet back to the return location.";
      case DELIVERY_STATUS.RETURN_ARRIVED:
        return "Your driver has arrived back at the return location.";
      case DELIVERY_STATUS.COMPLETED:
        return "Delivery completed! Your pet is safely back home.";
      case DELIVERY_STATUS.CANCELLED:
        return "This delivery was cancelled. Please check details.";
      default:
        return "Status unknown. Please check back later.";
    }
  };


  useEffect(() => {
    setMapData((prev) => ({
      ...prev,
      center_latitude: delivery.pickup_location.latitude,
      center_longitude: delivery.pickup_location.longitude,
      zoom: 14,
    }));

    const timeoutId = setTimeout(() => {
      if (
        status == DELIVERY_STATUS.REQUESTED ||
        status == DELIVERY_STATUS.FINDING
      ) {
        setMapData((prev) => ({
          ...prev,
          pickup_latitude: delivery.pickup_location.latitude,
          pickup_longitude: delivery.pickup_location.longitude,
          pickup_address: delivery.pickup_location.address,
          dropoff_latitude: delivery.dropoff_location.latitude,
          dropoff_longitude: delivery.dropoff_location.longitude,
          dropoff_address: delivery.dropoff_location.address,
        }));
      }

      if (
        [
          DELIVERY_STATUS.PICKUP_ARRIVED,
          DELIVERY_STATUS.ASSIGNED,
          DELIVERY_STATUS.HEADING_TO_PICKUP,
          DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF,
          DELIVERY_STATUS.DROPOFF_ARRIVED,
          DELIVERY_STATUS.EN_ROUTE_RETURN,
          DELIVERY_STATUS.RETURN_ARRIVED
        ].includes(status)
      ) {
        if (delivery && delivery.pickup_location && delivery.dropoff_location) {
          setMapData((prev) => ({
            ...prev,
            pickup_latitude: delivery.pickup_location.latitude,
            pickup_longitude: delivery.pickup_location.longitude,
            pickup_address: delivery.pickup_location.address,
            dropoff_latitude: delivery.dropoff_location.latitude,
            dropoff_longitude: delivery.dropoff_location.longitude,
            dropoff_address: delivery.dropoff_location.address,
          }));
        }

        if (delivery && delivery.driver.pin_location) {
          setMapData((prev) => ({
            ...prev,
            driver_latitude: delivery.driver.pin_location.latitude,
            driver_longitude: delivery.driver.pin_location.longitude,
            driver_address: delivery.driver.pin_location.address,
          }));
        }
      }


    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  // Second useEffect to trigger calculateRoute after mapData is fully set
  useEffect(() => {
    if (
      mapData.pickup_latitude &&
      mapData.driver_latitude &&
      [DELIVERY_STATUS.HEADING_TO_PICKUP, DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF, DELIVERY_STATUS.EN_ROUTE_RETURN].includes(status)
    ) {
      const isDrop = status === DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF;

      const routeTimeoutId = setTimeout(() => {
        calculateRoute(isDrop);
      }, 1000);

      return () => clearTimeout(routeTimeoutId);
    }
  }, [mapData]);


  useEffect(() => {
    const interval = setInterval(() => {

      if (status == DELIVERY_STATUS.FINDING) {
        setMapData((prev) => {
          const isCurrentlyPickup =
            prev.center_latitude === delivery.pickup_location.latitude &&
            prev.center_longitude === delivery.pickup_location.longitude;

          return {
            ...prev,
            center_latitude: isCurrentlyPickup
              ? delivery.dropoff_location.latitude
              : delivery.pickup_location.latitude,
            center_longitude: isCurrentlyPickup
              ? delivery.dropoff_location.longitude
              : delivery.pickup_location.longitude,
          };
        });
      }


      if (status == DELIVERY_STATUS.ASSIGNED) {
        setMapData((prev) => {
          const isCurrentlyPickup =
            prev.center_latitude === delivery.pickup_location.latitude &&
            prev.center_longitude === delivery.pickup_location.longitude;

          return {
            ...prev,
            center_latitude: isCurrentlyPickup
              ? delivery.driver.pin_location.latitude
              : delivery.pickup_location.latitude,
            center_longitude: isCurrentlyPickup
              ? delivery.driver.pin_location.longitude
              : delivery.pickup_location.longitude,
          };
        });
      }


    }, 10000);

    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, [delivery])



  const calculateRoute = (isDrop = false) => {
    if (!mapData.driver_latitude || !mapData.driver_longitude) return;

    const destinationLat = isDrop ? mapData.dropoff_latitude : mapData.pickup_latitude;
    const destinationLng = isDrop ? mapData.dropoff_longitude : mapData.pickup_longitude;

    if (!destinationLat || !destinationLng) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: mapData.driver_latitude,
          lng: mapData.driver_longitude,
        },
        destination: {
          lat: destinationLat,
          lng: destinationLng,
        },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          setDistance(result.routes[0].legs[0].distance.text);
          setDuration(result.routes[0].legs[0].duration.text);
        } else {
          // Error fetching directions
        }
      }
    );
  };



  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div className="delivery-map-status mb-6">
      <StatusMessage status={status} message={handleStatusFlow()} type="delivery" />
      <div
        className={` ${mapHidden.includes(status) ? "hidden" : "block"}`}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{
            lat: mapData.center_latitude,
            lng: mapData.center_longitude,
          }}
          zoom={mapData.zoom}
          options={{
            styles: GoogleMapStyle,
            disableDefaultUI: true,
            zoomControl: true,
          }}
        >
          {[DELIVERY_STATUS.FINDING, DELIVERY_STATUS.REQUESTED].includes(status) && (
            <>
              {mapData.pickup_latitude && (
                <>
                  <Marker
                    position={{
                      lat: mapData.pickup_latitude,
                      lng: mapData.pickup_longitude,
                    }}
                    icon={{
                      url: PickupLocationIcon,
                      scaledSize: new window.google.maps.Size(56, 76),
                    }}
                    animation={"BOUNCE"}
                  />

                  <Marker
                    position={{
                      lat: mapData.dropoff_latitude,
                      lng: mapData.dropoff_longitude,
                    }}
                    icon={{
                      url: DropoffLocationIcon,
                      scaledSize: new window.google.maps.Size(46, 66),
                    }}
                    animation={"BOUNCE"}
                  />

                  <OverlayView
                    position={{
                      lat: mapData.pickup_latitude,
                      lng: mapData.pickup_longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="absolute w-36 h-36 rounded-full bg-primary opacity-50 animate-pulse-circle-delay-2"></div>
                    </div>
                  </OverlayView>
                </>
              )}
            </>
          )}

          {(status == DELIVERY_STATUS.ASSIGNED) && (
            <>
              {mapData.pickup_latitude && (
                <>
                  <Marker
                    position={{
                      lat: mapData.pickup_latitude,
                      lng: mapData.pickup_longitude,
                    }}
                    icon={{
                      url: PickupLocationIcon,
                      scaledSize: new window.google.maps.Size(56, 76),
                    }}
                  />

                  <OverlayView
                    position={{
                      lat: mapData.pickup_latitude,
                      lng: mapData.pickup_longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="absolute w-36 h-36 rounded-full bg-primary opacity-50 animate-pulse-circle-delay-2"></div>
                    </div>
                  </OverlayView>
                </>
              )}

              {mapData.driver_latitude && (
                <>
                  <Marker
                    position={{
                      lat: mapData.driver_latitude,
                      lng: mapData.driver_longitude,
                    }}
                    icon={{
                      url: DriverLocationIcon,
                      scaledSize: new window.google.maps.Size(36, 46), // Ensure the icon is scaled to the right size
                    }}
                  />
                  <OverlayView
                    position={{
                      lat: mapData.driver_latitude,
                      lng: mapData.driver_longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="absolute w-36 h-36 rounded-full bg-tblue opacity-50 animate-pulse-circle-delay-1"></div>
                      <div className="absolute w-12 h-12 rounded-full bg-tblue opacity-50 animate-pulse-circle-delay-3"></div>
                    </div>
                  </OverlayView>
                </>
              )}
            </>
          )}


          {[DELIVERY_STATUS.HEADING_TO_PICKUP, DELIVERY_STATUS.PICKUP_ARRIVED, DELIVERY_STATUS.RETURN_ARRIVED, DELIVERY_STATUS.EN_ROUTE_RETURN].includes(status) && (
            <>
              {mapData.pickup_latitude && (
                <Marker
                  position={{
                    lat: mapData.pickup_latitude,
                    lng: mapData.pickup_longitude,
                  }}
                  icon={{
                    url: PickupLocationIcon,
                    scaledSize: new window.google.maps.Size(56, 76),
                  }}
                />
              )}
              {mapData.driver_latitude && (
                <Marker
                  position={{
                    lat: mapData.driver_latitude,
                    lng: mapData.driver_longitude,
                  }}
                  icon={{
                    url: DriverLocationIcon,
                    scaledSize: new window.google.maps.Size(36, 46), // Ensure the icon is scaled to the right size
                  }}
                />
              )}
              {directionsResponse && (
                <DirectionsRenderer
                  directions={directionsResponse}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: "#9A48FF",
                      strokeOpacity: 1,
                      strokeWeight: 5,
                    },
                  }}
                />
              )}
            </>
          )}

          {[DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF, DELIVERY_STATUS.DROPOFF_ARRIVED].includes(status) && (
            <>
              {mapData.dropoff_latitude && (
                <Marker
                  position={{
                    lat: mapData.dropoff_latitude,
                    lng: mapData.dropoff_longitude,
                  }}
                  icon={{
                    url: DropoffLocationIcon,
                    scaledSize: new window.google.maps.Size(56, 76),
                  }}
                />
              )}
              {mapData.driver_latitude && (
                <Marker
                  position={{
                    lat: mapData.driver_latitude,
                    lng: mapData.driver_longitude,
                  }}
                  icon={{
                    url: DriverLocationIcon,
                    scaledSize: new window.google.maps.Size(36, 46), // Ensure the icon is scaled to the right size
                  }}
                />
              )}
              {directionsResponse && (
                <DirectionsRenderer
                  directions={directionsResponse}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: "#9A48FF",
                      strokeOpacity: 1,
                      strokeWeight: 5,
                    },
                  }}
                />
              )}
            </>
          )}


          {/* {status == DELIVERY_STATUS.IN_PROGRESS && (
            <>
              {mapData.driver_latitude && (
                <>
                  <Marker
                    position={{
                      lat: mapData.driver_latitude,
                      lng: mapData.driver_longitude,
                    }}
                    icon={{
                      url: WalkInProgressIcon,
                      scaledSize: new window.google.maps.Size(50, 50),
                    }}
                    animation={"BOUNCE"}
                  />

                  <OverlayView
                    position={{
                      lat: mapData.driver_latitude,
                      lng: mapData.driver_longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="absolute w-16 h-16 rounded-full bg-primary opacity-30 animate-pulse-circle-delay-1"></div>
                    </div>
                  </OverlayView>
                </>
              )}
            </>
          )} */}
        </GoogleMap>
      </div>

      {[DELIVERY_STATUS.HEADING_TO_PICKUP, DELIVERY_STATUS.EN_ROUTE_TO_DROPOFF, DELIVERY_STATUS.EN_ROUTE_RETURN].includes(status) ? (
        <div className="mt-4 text-center text-gray-700">
          <FontAwesomeIcon icon={faDriversLicense} className="mr-2 text-purple-600" />
          Driver is approximately{" "}
          {<span className="font-bold text-green-400">{distance}</span> ||
            "calculating..."}{" "}
          away and will arrive in approximately{" "}
          {<span className="font-bold text-primary">{duration}</span> ||
            "calculating..."}{" "}
        </div>
      ) : null}
    </div>
  );
};

export default DeliveryMapStatus;
